import { ApiClientError, NetworkError } from '@recipes/api-client';
import type { Visit } from '@recipes/contracts';
import { api } from '@/lib/api';
import { offlineDb, type PendingVisit } from '@/lib/offline/db';
import { getPlatform } from '@/lib/platform';

/**
 * Capture queue for tasting notes (docs/finished/tasting-notes-spec.md 5.4). Every capture is written
 * to IndexedDB first and then synchronized, so a dish is never lost to bad reception. The
 * pending id is the idempotency key of the create request: however often the sync is
 * retried, the visit exists exactly once.
 */

export type SaveOutcome =
  { status: 'synced'; visit: Visit } | { status: 'queued'; pendingId: string };

export interface SyncResult {
  synced: Visit[];
  failed: number;
  offline: boolean;
}

const listeners = new Set<(result: SyncResult) => void>();
let running: Promise<SyncResult> | null = null;
let started = false;

export function subscribeTastingSync(listener: (result: SyncResult) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function isPermanent(error: unknown): boolean {
  return (
    error instanceof ApiClientError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429
  );
}

/** Uploads one dish photo and resolves with its asset id. Network loss → NetworkError. */
export async function uploadTastingPhoto(photo: {
  blob: Blob;
  name: string;
  mimeType: string;
  altText: string | null;
}): Promise<string> {
  const intent = await api.media.createUploadIntent({
    fileName: photo.name || 'photo.jpg',
    mimeType: photo.mimeType || 'image/jpeg',
    sizeBytes: photo.blob.size,
    purpose: 'TASTING_NOTE',
  });
  try {
    await api.media.upload(intent, photo.blob);
  } catch (error) {
    // fetch() rejects with a TypeError when the network is gone.
    throw error instanceof TypeError ? new NetworkError(error) : error;
  }
  const asset = await api.media.complete(intent.assetId, { altText: photo.altText });
  return asset.id;
}

/** Uploads the photos that have no asset yet and remembers each asset id right away. */
async function uploadPhotos(pending: PendingVisit): Promise<void> {
  for (const note of pending.notes) {
    for (const photo of note.photos) {
      if (photo.assetId) continue;
      photo.assetId = await uploadTastingPhoto(photo);
      await offlineDb.pendingVisits.put(pending);
    }
  }
}

async function syncOne(pending: PendingVisit): Promise<Visit> {
  await uploadPhotos(pending);
  const notes = pending.notes.map((n) => ({
    dishName: n.dishName,
    notes: n.notes,
    flavorTags: n.flavorTags,
    rating: n.rating,
    photos: n.photos.flatMap((p) =>
      p.assetId ? [{ assetId: p.assetId, altText: p.altText }] : [],
    ),
  }));
  if (pending.visitId) {
    let visit: Visit | null = null;
    for (const [index, note] of notes.entries())
      visit = await api.visits.addNote(pending.visitId, note, `${pending.id}:${index}`);
    return visit ?? (await api.visits.get(pending.visitId));
  }
  const hasPosition = pending.lat !== null && pending.lng !== null;
  return api.visits.create(
    {
      placeId: pending.placeId,
      visitedAt: pending.visitedAt,
      ...(hasPosition
        ? {
            lat: pending.lat,
            lng: pending.lng,
            accuracyM: pending.accuracyM,
            locationSource: pending.locationSource,
          }
        : {}),
      notes,
    },
    pending.id,
  );
}

/** Synchronizes queued visits in order. Stops at the first network failure. */
export function syncPendingVisits(): Promise<SyncResult> {
  if (running) return running;
  running = (async () => {
    const result: SyncResult = { synced: [], failed: 0, offline: false };
    const queue = await offlineDb.pendingVisits.orderBy('createdAt').toArray();
    for (const pending of queue) {
      try {
        const visit = await syncOne(pending);
        await offlineDb.pendingVisits.delete(pending.id);
        result.synced.push(visit);
      } catch (error) {
        // Offline, or not signed in yet (token refresh still in flight right after
        // reconnecting): nothing is wrong with the capture itself – wait for the next trigger.
        if (
          error instanceof NetworkError ||
          (error instanceof ApiClientError && error.status === 401)
        ) {
          result.offline = true;
          break;
        }
        // Never drop what the user captured: keep it with the error for a manual retry.
        await offlineDb.pendingVisits.update(pending.id, {
          attempts: pending.attempts + 1,
          lastError: error instanceof Error ? error.message : String(error),
        });
        result.failed += 1;
        if (!isPermanent(error)) break;
      }
    }
    listeners.forEach((l) => l(result));
    return result;
  })().finally(() => {
    running = null;
  });
  return running;
}

/** Stores the capture locally, then tries to synchronize it immediately. */
export async function saveCapture(
  capture: Omit<PendingVisit, 'id' | 'createdAt' | 'attempts'>,
): Promise<SaveOutcome> {
  const pending: PendingVisit = {
    ...capture,
    id: crypto.randomUUID(),
    createdAt: Date.now(),
    attempts: 0,
  };
  await offlineDb.pendingVisits.add(pending);
  const platform = await getPlatform();
  if (!platform.isOnline()) return { status: 'queued', pendingId: pending.id };
  const result = await syncPendingVisits();
  const stillQueued = await offlineDb.pendingVisits.get(pending.id);
  if (stillQueued) {
    // A server-side rejection (validation, permissions) is an error the user has to see.
    if (!result.offline && stillQueued.lastError) throw new Error(stillQueued.lastError);
    return { status: 'queued', pendingId: pending.id };
  }
  // The visit this capture ended up in is the last one synchronized.
  const visit = result.synced[result.synced.length - 1];
  return visit ? { status: 'synced', visit } : { status: 'queued', pendingId: pending.id };
}

export async function discardPendingVisit(id: string): Promise<void> {
  await offlineDb.pendingVisits.delete(id);
}

/** Idempotent: synchronizes when connectivity returns and when the app is resumed. */
export function startTastingSync(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  void getPlatform().then((platform) => {
    platform.onConnectivityChange((online) => {
      if (online) void syncPendingVisits();
    });
    platform.onResume(() => {
      if (platform.isOnline()) void syncPendingVisits();
    });
    if (platform.isOnline()) void syncPendingVisits();
  });
}
